import { format } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/app/store';
import {
  formatTimeDisplay,
  getDateFnsLocale,
  parseDateKey,
  shiftDateKey,
  todayKey,
} from '@/domain/dates';
import { computeFreeIntervals, filterPlans, getDisplayTitle } from '@/domain/plans';
import { partnerOf } from '@/domain/helpers';
import type { Plan } from '@/domain/types';
import { PlanChip } from '@/features/plans/PlanChip';
import { QuickAdd } from '@/features/plans/QuickAdd';
import { useLayoutMode } from '@/platform/useLayoutMode';
import styles from './TodayView.module.css';

const HOUR_START = 7;
const HOUR_END = 23;
const HOUR_PX = 46;

function timeToTop(time: string | null): number {
  if (!time) return 0;
  const [h, m] = time.split(':').map(Number);
  return ((h - HOUR_START) * 60 + (m || 0)) * (HOUR_PX / 60);
}

function durationPx(plan: Plan): number {
  if (plan.isAllDay || !plan.startTime || !plan.endTime) return HOUR_PX;
  return Math.max(36, timeToTop(plan.endTime) - timeToTop(plan.startTime));
}

function overlapFree(
  a: { start: string; end: string }[],
  b: { start: string; end: string }[],
): { start: string; end: string }[] {
  const result: { start: string; end: string }[] = [];
  for (const left of a) {
    for (const right of b) {
      const start = left.start > right.start ? left.start : right.start;
      const end = left.end < right.end ? left.end : right.end;
      if (start < end) result.push({ start, end });
    }
  }
  return result;
}

export function TodayView() {
  const { t, i18n } = useTranslation();
  const layoutMode = useLayoutMode();
  const isMobile = layoutMode === 'mobile';
  const session = useAppStore((s) => s.session);
  const profiles = useAppStore((s) => s.profiles);
  const plans = useAppStore((s) => s.plans);
  const filters = useAppStore((s) => s.filters);
  const preferences = useAppStore((s) => s.preferences);
  const openPlanEditor = useAppStore((s) => s.openPlanEditor);

  const viewerId = session?.user.id ?? '';
  const today = todayKey();
  const timeFormat = preferences?.timeFormat ?? '24h';
  const locale = i18n.language.startsWith('ru') ? 'ru' : 'en';
  const partner = session ? partnerOf(profiles, session.user.id) : null;
  const me = profiles.find((p) => p.id === viewerId);

  const visiblePlans = useMemo(() => {
    if (!viewerId) return [];
    return filterPlans(
      plans.filter((p) => !p.deletedAt),
      filters,
      viewerId,
    );
  }, [plans, filters, viewerId]);

  const todayPlans = useMemo(
    () => visiblePlans.filter((p) => p.planDate === today && p.status === 'active'),
    [visiblePlans, today],
  );

  const myAll = todayPlans.filter((p) => p.ownerId === viewerId);
  const partnerAll = todayPlans.filter((p) => p.ownerId !== viewerId);
  const sharedPlans = todayPlans.filter((p) => p.visibility === 'shared');

  const upcomingEnd = shiftDateKey(today, 7);
  const upcoming = useMemo(
    () =>
      visiblePlans.filter(
        (p) =>
          p.status === 'active' &&
          p.planDate &&
          p.planDate > today &&
          p.planDate <= upcomingEnd,
      ),
    [visiblePlans, today, upcomingEnd],
  );

  const freeMine = useMemo(() => computeFreeIntervals(myAll), [myAll]);
  const freePartner = useMemo(() => computeFreeIntervals(partnerAll), [partnerAll]);
  const freeTogether = useMemo(
    () => (partner ? overlapFree(freeMine, freePartner) : freeMine),
    [freeMine, freePartner, partner],
  );

  const hours = Array.from({ length: HOUR_END - HOUR_START + 1 }, (_, i) => HOUR_START + i);
  const timelineHeight = (HOUR_END - HOUR_START) * HOUR_PX;
  const todayFull = format(parseDateKey(today), 'EEEE, d MMMM yyyy', {
    locale: getDateFnsLocale(locale),
  });

  const renderLaneBlock = (plan: Plan, lane: 'me' | 'partner' | 'shared') => {
    const top = plan.isAllDay || !plan.startTime ? 8 : timeToTop(plan.startTime);
    const height = durationPx(plan);
    const title = getDisplayTitle(plan, viewerId, t('availability.busy'));
    const timeLabel =
      plan.isAllDay
        ? t('plan.allDay')
        : `${formatTimeDisplay(plan.startTime, timeFormat)}${
            plan.endTime ? ` – ${formatTimeDisplay(plan.endTime, timeFormat)}` : ''
          }`;
    return (
      <button
        key={plan.id}
        type="button"
        className={
          lane === 'shared'
            ? styles.sharedBlock
            : lane === 'me'
              ? styles.meBlock
              : styles.partnerBlock
        }
        style={{ top, height }}
        onClick={() => openPlanEditor(plan.id)}
      >
        <span className={styles.blockRail} aria-hidden />
        <span className={styles.blockTitle} title={title}>
          {title}
        </span>
        {(lane === 'shared' ||
          (plan.hideDetailsFromPartner && plan.ownerId === viewerId) ||
          (plan.availabilityStatus === 'busy' && plan.ownerId !== viewerId)) && (
          <span className={styles.blockMeta}>
            {lane === 'shared' && (
              <span className={styles.togetherBadge}>{t('app.together')}</span>
            )}
            {plan.hideDetailsFromPartner && plan.ownerId === viewerId && (
              <span className={styles.busyBadge}>{t('plan.hideDetails')}</span>
            )}
            {plan.availabilityStatus === 'busy' && plan.ownerId !== viewerId && (
              <span className={styles.busyBadge}>{t('availability.busy')}</span>
            )}
          </span>
        )}
        <span className={`${styles.blockTime} mono`}>{timeLabel}</span>
      </button>
    );
  };

  if (isMobile) {
    return (
      <div className={styles.view}>
        <header className={styles.mobileHeader}>
          <h1 className={styles.screenTitle}>{t('nav.today')}</h1>
          <p className={`${styles.dateLine} mono`}>{todayFull}</p>
        </header>

        <section className={styles.section}>
          <h2 className="label-mono">{me?.displayName ?? t('app.myPlans')}</h2>
          <div className={styles.rows}>
            {myAll.length === 0 ? (
              <p className={styles.emptyRow}>{t('common.empty')}</p>
            ) : (
              myAll.map((p) => <PlanChip key={p.id} plan={p} compact />)
            )}
          </div>
        </section>

        {partner && (
          <section className={styles.section}>
            <h2 className="label-mono">{partner.displayName}</h2>
            <div className={styles.rows}>
              {partnerAll.length === 0 ? (
                <p className={styles.emptyRow}>{t('common.empty')}</p>
              ) : (
                partnerAll.map((p) => <PlanChip key={p.id} plan={p} compact />)
              )}
            </div>
          </section>
        )}

        <section className={styles.section}>
          <h2 className="label-mono">{t('app.sharedPlans')}</h2>
          <div className={styles.rows}>
            {sharedPlans.length === 0 ? (
              <p className={styles.emptyRow}>{t('common.empty')}</p>
            ) : (
              sharedPlans.map((p) => <PlanChip key={p.id} plan={p} compact shared />)
            )}
          </div>
        </section>

        {freeTogether.length > 0 && (
          <section className={styles.section}>
            <h2 className="label-mono">{t('app.freeTogether')}</h2>
            <ul className={styles.freeList}>
              {freeTogether.map((slot) => (
                <li key={`${slot.start}-${slot.end}`} className={styles.freeItem}>
                  <span className="mono">
                    {formatTimeDisplay(slot.start, timeFormat)} –{' '}
                    {formatTimeDisplay(slot.end, timeFormat)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <QuickAdd date={today} className={styles.quickAdd} />
      </div>
    );
  }

  return (
    <div className={styles.desktopView}>
      <header className={styles.desktopHeader}>
        <div>
          <h1 className={styles.screenTitle}>{t('nav.today')}</h1>
          <p className={`${styles.dateLine} mono`}>{todayFull}</p>
        </div>
        <div className={styles.legend}>
          <span>
            <i style={{ background: me?.accentColor ?? 'var(--accent)' }} />
            {me?.displayName ?? '—'}
          </span>
          {partner && (
            <span>
              <i style={{ background: partner.accentColor }} />
              {partner.displayName}
            </span>
          )}
          <span>
            <i className={styles.sharedSwatch} />
            {t('app.together')}
          </span>
        </div>
      </header>

      <div className={styles.desktopBody}>
        <div className={styles.timelineCard}>
          <div className={styles.laneHeaders}>
            <div className="label-mono">{me?.displayName ?? t('app.myPlans')}</div>
            <div />
            <div className={`${styles.laneRight} label-mono`}>
              {partner?.displayName ?? t('app.partnerPlans')}
            </div>
          </div>

          <div className={styles.timeline} style={{ height: timelineHeight }}>
            {hours.map((h) => (
              <div
                key={h}
                className={styles.hourLine}
                style={{ top: (h - HOUR_START) * HOUR_PX }}
              >
                <span className={`${styles.hourLabel} mono`}>
                  {String(h).padStart(2, '0')}:00
                </span>
              </div>
            ))}

            <div className={styles.laneMe}>
              {myAll
                .filter((p) => p.visibility !== 'shared')
                .map((p) => renderLaneBlock(p, 'me'))}
            </div>

            <div className={styles.laneShared}>
              {sharedPlans.map((p) => renderLaneBlock(p, 'shared'))}
            </div>

            <div className={styles.lanePartner}>
              {partnerAll
                .filter((p) => p.visibility !== 'shared')
                .map((p) => renderLaneBlock(p, 'partner'))}
            </div>
          </div>
        </div>

        <aside className={styles.sideRail}>
          <div className={styles.freeCard}>
            <div className={styles.freeCardHead}>
              <span className={styles.sharedSwatch} />
              <span className="label-mono">{t('app.freeTogether')}</span>
            </div>
            <p className={styles.freeHint}>{t('app.freeTogetherHint')}</p>
            {freeTogether.length === 0 ? (
              <p className={styles.emptyRow}>{t('common.empty')}</p>
            ) : (
              freeTogether.slice(0, 4).map((slot) => (
                <button
                  key={`${slot.start}-${slot.end}`}
                  type="button"
                  className={styles.freeSlotBtn}
                  onClick={() => openPlanEditor(null)}
                >
                  <span className="mono">
                    {formatTimeDisplay(slot.start, timeFormat)} –{' '}
                    {formatTimeDisplay(slot.end, timeFormat)}
                  </span>
                  <span aria-hidden>+</span>
                </button>
              ))
            )}
          </div>

          <QuickAdd date={today} className={styles.quickAdd} />

          {upcoming.length > 0 && (
            <div className={styles.upcomingCard}>
              <h2 className="label-mono">{t('app.upcoming')}</h2>
              <div className={styles.rows}>
                {upcoming.slice(0, 5).map((plan) => (
                  <PlanChip key={plan.id} plan={plan} compact />
                ))}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
